import React, { useEffect, useState } from 'react';
import { IconButton, IconTextButton } from '../ui';

const AssignEmployeeZonesTable = ({ 
  assignedEmployeesToZones, 
  onAddEmployeeToZone, 
  onRemoveEmployeeFromZone, 
  onDeleteEmployeeFromZone,
  onNoteChange
}) => {
  const [zoneNotes, setZoneNotes] = useState({});

  // Initialize zone notes when employees are assigned to zones
  useEffect(() => {
    const newNotes = {};
    // Handle both array and object formats for backward compatibility
    if (Array.isArray(assignedEmployeesToZones)) {
      assignedEmployeesToZones.forEach((employee, index) => {
        const employeeKey = typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id || index;
        
        // Check for existing notes from backend data
        const existingNote = employee?.notes || employee?.user_notes || employee?.note || '';
        
        // Only set the note if it exists (don't set placeholder as value)
        if (existingNote) {
          newNotes[employeeKey] = existingNote;
        }
      });
    } else if (typeof assignedEmployeesToZones === 'object' && assignedEmployeesToZones !== null) {
      // Handle object format where keys are zone IDs and values are arrays of employees
      Object.values(assignedEmployeesToZones).forEach(zoneEmployees => {
        if (Array.isArray(zoneEmployees)) {
          zoneEmployees.forEach((employee, index) => {
            const employeeKey = typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id || index;
            
            // Check for existing notes from backend data
            const existingNote = employee?.notes || employee?.user_notes || employee?.note || '';
            
            // Only set the note if it exists (don't set placeholder as value)
            if (existingNote) {
              newNotes[employeeKey] = existingNote;
            }
          });
        }
      });
    }
    
    // Update the notes state with the current employee data
    setZoneNotes(prev => {
      // Only update if there are actual changes to avoid unnecessary re-renders
      const hasChanges = Object.keys(newNotes).some(key => prev[key] !== newNotes[key]);
      return hasChanges ? { ...prev, ...newNotes } : prev;
    });
  }, [assignedEmployeesToZones]);

  const handleZoneNoteChange = (employee, newNote) => {
    const employeeKey = typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id;
    setZoneNotes(prev => ({
      ...prev,
      [employeeKey]: newNote
    }));
    
    // Update the employee object with the new note and call parent callback
    const updatedEmployee = {
      ...employee,
      notes: newNote,
      user_notes: newNote
    };
    
    // Call parent callback if provided
    if (onNoteChange) {
      onNoteChange(updatedEmployee, newNote);
    }
  };

  const handlePasteToAll = (sourceEmployee, sourceNote) => {
    // Only paste if the source note is not empty
    if (!sourceNote || sourceNote.trim() === '') {
      return;
    }

    // Get the source employee key
    const sourceEmployeeKey = typeof sourceEmployee === 'string' ? sourceEmployee : sourceEmployee?.user_id || sourceEmployee?.employee_id;
    
    // Get all employees from assignedEmployeesToZones
    const allEmployees = [];
    if (Array.isArray(assignedEmployeesToZones)) {
      assignedEmployeesToZones.forEach((employee) => {
        allEmployees.push(employee);
      });
    } else if (typeof assignedEmployeesToZones === 'object' && assignedEmployeesToZones !== null) {
      Object.values(assignedEmployeesToZones).forEach(zoneEmployees => {
        if (Array.isArray(zoneEmployees)) {
          zoneEmployees.forEach((employee) => {
            allEmployees.push(employee);
          });
        }
      });
    }

    // Update notes for all employees who have empty notes
    const updatedNotes = { ...zoneNotes };
    let hasUpdates = false;

    allEmployees.forEach((employee) => {
      const employeeKey = typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id;
      
      // Skip the source employee
      if (employeeKey === sourceEmployeeKey) {
        return;
      }

      // Get current note for this employee
      const currentNote = updatedNotes[employeeKey] || '';
      
      // Only update if the note is empty
      if (!currentNote || currentNote.trim() === '') {
        updatedNotes[employeeKey] = sourceNote;
        hasUpdates = true;
        
        // Update the employee object and call parent callback
        const updatedEmployee = {
          ...employee,
          notes: sourceNote,
          user_notes: sourceNote
        };
        
        if (onNoteChange) {
          onNoteChange(updatedEmployee, sourceNote);
        }
      }
    });

    // Update local state if there were any changes
    if (hasUpdates) {
      setZoneNotes(updatedNotes);
    }
  };

  const handleDeleteEmployeeFromZone = (index) => {
    console.log('Delete employee from zone clicked for employee at index:', index);
    const employeeToDelete = assignedEmployeesToZones[index];
    console.log('Employee to delete from zone:', employeeToDelete);
    
    // Remove zone note from local state
    const employeeKey = typeof employeeToDelete === 'string' ? employeeToDelete : employeeToDelete?.user_id || employeeToDelete?.employee_id;
    setZoneNotes(prev => {
      const newNotes = { ...prev };
      delete newNotes[employeeKey];
      return newNotes;
    });
    
    // Call the parent's remove function
    console.log('Calling onRemoveEmployeeFromZone with index:', index);
    onRemoveEmployeeFromZone(index);
  };

  return (
    <div className="self-stretch flex flex-col justify-start items-start gap-6">
      {/* Assign Employees to Zones Heading and Add Employee Button */}
      <div className="self-stretch flex flex-col justify-start items-start gap-2">
        <div className="self-stretch inline-flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-0">
          <div className="justify-start text-zinc-500 text-xl font-bold font-['Figtree'] leading-normal">
            Assign Employees to Zones
          </div>
          <IconTextButton 
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4V20M4 12H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            text="Add Employee"
            onClick={onAddEmployeeToZone}
            className="w-full md:w-auto md:min-w-[12rem] h-12"
          />
        </div>

        {/* Show message when no employees are assigned to zones */}
        {(!assignedEmployeesToZones || 
          (Array.isArray(assignedEmployeesToZones) && assignedEmployeesToZones.length === 0) ||
          (typeof assignedEmployeesToZones === 'object' && Object.keys(assignedEmployeesToZones).length === 0)) && (
          <div className="self-stretch p-3 bg-neutral-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-white/0 flex flex-col justify-start items-start gap-2.5">
            <div className="justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
              No Employee has been assigned to zones
            </div>
          </div>
        )}
      </div>

      {/* Employee and Zone Table - Only show when employees are assigned to zones */}
      {assignedEmployeesToZones && 
       ((Array.isArray(assignedEmployeesToZones) && assignedEmployeesToZones.length > 0) ||
        (typeof assignedEmployeesToZones === 'object' && Object.keys(assignedEmployeesToZones).length > 0)) && (
        <div className="self-stretch rounded-xl shadow-[-2px_-2px_6px_0px_rgba(211,211,211,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] outline outline-1 outline-offset-[-1px] outline-cyan-700/10 backdrop-blur-[20px] inline-flex flex-col justify-start items-start overflow-hidden">
          {/* Table Header */}
          <div className="self-stretch bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] border-b border-black inline-flex justify-between items-center overflow-hidden">
            <div className="flex-1 h-16 relative overflow-hidden">
              <div className="left-[16px] top-[20px] absolute justify-start text-black text-xl font-bold font-['Figtree'] leading-normal">
                Employee
              </div>
            </div>
            <div className="flex-1 h-16 relative overflow-hidden">
              <div className="left-[16px] top-[20px] absolute justify-start text-black text-xl font-bold font-['Figtree'] leading-normal">
                Zone Note
              </div>
            </div>
          </div>

          {/* Table Rows - Dynamic based on employees assigned to zones */}
          {(() => {
            // Flatten employees from all zones into a single array for rendering
            const allEmployees = [];
            if (Array.isArray(assignedEmployeesToZones)) {
              assignedEmployeesToZones.forEach((employee, index) => {
                allEmployees.push({ employee, index, zoneId: null });
              });
            } else if (typeof assignedEmployeesToZones === 'object' && assignedEmployeesToZones !== null) {
              Object.entries(assignedEmployeesToZones).forEach(([zoneId, zoneEmployees]) => {
                if (Array.isArray(zoneEmployees)) {
                  zoneEmployees.forEach((employee, index) => {
                    allEmployees.push({ employee, index, zoneId });
                  });
                }
              });
            }
            return allEmployees;
          })().map(({ employee, index, zoneId }) => (
            <div key={index} className="self-stretch bg-white border-b border-zinc-300 inline-flex justify-between items-center overflow-hidden">
              <div className="flex-1 h-14 relative overflow-hidden">
                <div className="left-[16px] top-[16px] absolute justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
                  {typeof employee === 'string' ? employee : employee?.display_name || employee?.user_name || 'Unknown Employee'}
                </div>
                <IconButton 
                  onClick={() => handleDeleteEmployeeFromZone(index)}
                  icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6L18 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  }
                  className="absolute right-4 top-3 hover:bg-red-600 transition-colors duration-200 cursor-pointer"
                  title="Remove employee from zone"
                />
              </div>
              <div className="flex-1 h-14 relative overflow-hidden">
                <input
                  type="text"
                  value={zoneNotes[typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id] || ''}
                  onChange={(e) => handleZoneNoteChange(employee, e.target.value)}
                  className="left-[16px] top-[16px] absolute justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed bg-transparent border-none outline-none w-3/4"
                  placeholder="Add zone note"
                />
                {/* Paste to All button - only show if employee has a note */}
                {(() => {
                  const employeeKey = typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id;
                  const currentNote = zoneNotes[employeeKey] || '';
                  const hasNote = currentNote && currentNote.trim() !== '';
                  
                  return hasNote ? (
                    <IconButton
                      onClick={() => handlePasteToAll(employee, currentNote)}
                      icon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <rect x="8" y="2" width="8" height="4" rx="1" ry="1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      }
                      className="absolute right-4 top-3 hover:bg-green-600 transition-colors duration-200"
                      title="Paste note to all employees with empty notes"
                    />
                  ) : null;
                })()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssignEmployeeZonesTable;
